import { Trans, useLingui } from "@lingui/react/macro";
import { providerFullDisplayName } from "@pontimed/types";
import { ApiError, apiFetch } from "@pontimed/api-client";
import { cn } from "@pontimed/ui";
import { AnimatePresence, m } from "framer-motion";
import { createFileRoute } from "@tanstack/react-router";
import { Check, QrCode } from "lucide-react";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

interface ProviderDetail {
  id: string;
  firstName: string;
  lastName: string;
  profession?: string | null;
  photoUrl: string | null;
  specialty: { name: Record<string, string> | string } | null;
  defaultSlotDuration?: number | null;
}

export const Route = createFileRoute("/waiting-room/$providerId")({
  component: WaitingRoomPage,
});

function WaitingRoomPage() {
  const { providerId } = Route.useParams();
  const { i18n } = useLingui();
  const locale = i18n.locale;

  const [provider, setProvider] = useState<ProviderDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    apiFetch
      .get<ProviderDetail>(`/api/providers/${providerId}`, {
        signal: controller.signal,
        skipAuthRefresh: true,
      })
      .then((data) => setProvider(data))
      .catch((err: unknown) => {
        if (err instanceof DOMException && err.name === "AbortError") return;
        if (err instanceof ApiError && err.status === 404) {
          setError("provider-not-found");
        } else {
          setError(err instanceof Error ? err.message : "load-failed");
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [providerId]);

  return (
    <div className="bg-background flex min-h-dvh flex-col">
      <header className="border-border/50 border-b px-4 py-3">
        <div className="mx-auto flex max-w-md items-center justify-center">
          <span className="text-foreground text-base font-bold tracking-tight">PontiMed</span>
        </div>
      </header>

      <main className="mx-auto flex w-full max-w-md flex-1 flex-col gap-6 px-4 py-8">
        {loading && <WaitingSkeleton />}
        {!loading && error && <WaitingError code={error} />}
        {!loading && !error && provider && <WaitingRoomBody provider={provider} locale={locale} />}
      </main>

      <footer className="border-border/50 border-t px-4 py-4 text-center">
        <p className="text-muted-foreground text-xs">
          <Trans>Powered by Pontimed</Trans>
        </p>
      </footer>
    </div>
  );
}

function WaitingSkeleton() {
  return (
    <>
      <Skeleton className="h-24 w-full rounded-2xl" />
      <div className="flex justify-center">
        <Skeleton className="size-40 rounded-full" />
      </div>
      <Skeleton className="h-14 w-full rounded-2xl" />
    </>
  );
}

function WaitingError({ code }: { code: string }) {
  return (
    <div className="border-destructive/20 bg-card flex flex-col gap-3 rounded-2xl border p-6 text-center">
      <h2 className="text-foreground text-base font-semibold">
        {code === "provider-not-found" ? (
          <Trans>Provider not found</Trans>
        ) : (
          <Trans>Couldn't load waiting room</Trans>
        )}
      </h2>
    </div>
  );
}

function WaitingRoomBody({ provider, locale }: { provider: ProviderDetail; locale: string }) {
  const providerName = providerFullDisplayName(provider, locale);
  const specialtyName = pickLocalized(provider.specialty?.name ?? null, locale);
  const initial =
    provider.lastName?.[0]?.toUpperCase() ?? provider.firstName?.[0]?.toUpperCase() ?? "D";

  return (
    <>
      {/* Provider card */}
      <section className="bg-card border-border flex items-center gap-4 rounded-2xl border p-4 shadow-sm">
        {provider.photoUrl ? (
          <img
            src={provider.photoUrl}
            alt={providerName}
            className="size-16 shrink-0 rounded-full object-cover"
            loading="lazy"
          />
        ) : (
          <div className="bg-primary/10 flex size-16 shrink-0 items-center justify-center rounded-full">
            <span className="text-primary text-2xl font-bold">{initial}</span>
          </div>
        )}
        <div className="min-w-0">
          <p className="text-foreground truncate text-base font-semibold">{providerName}</p>
          {specialtyName && <p className="text-muted-foreground text-sm">{specialtyName}</p>}
          <div className="mt-1 flex items-center gap-1.5">
            <span className="relative flex size-2">
              <span className="bg-primary absolute inline-flex size-full animate-ping rounded-full opacity-75" />
              <span className="bg-primary relative inline-flex size-2 rounded-full" />
            </span>
            <span className="text-primary text-xs font-medium">
              <Trans>Live</Trans>
            </span>
          </div>
        </div>
      </section>

      {/* Hero status */}
      <section className="flex flex-col items-center gap-2 py-2">
        <p className="text-muted-foreground text-sm font-medium tracking-wider uppercase">
          <Trans>You're checked in</Trans>
        </p>
        <div className="border-primary/20 bg-primary/5 flex size-40 flex-col items-center justify-center rounded-full border-4 shadow-inner">
          <span className="text-primary text-4xl leading-none font-black">
            <Trans>Hi!</Trans>
          </span>
        </div>
        <p className="text-muted-foreground mt-2 max-w-xs text-center text-xs">
          <Trans>You'll be seen shortly. Please stay on this screen.</Trans>
        </p>
      </section>

      {/* Check in CTA */}
      <CheckInButton />

      {/* QR code placeholder */}
      <section className="bg-muted/40 border-border flex flex-col items-center gap-3 rounded-2xl border border-dashed p-6 text-center">
        <QrCode className="text-muted-foreground size-12" aria-hidden />
        <div>
          <p className="text-foreground text-sm font-medium">
            <Trans>Scan in the clinic</Trans>
          </p>
          <p className="text-muted-foreground mt-0.5 text-xs">
            <Trans>Show this QR to the receptionist on arrival.</Trans>
          </p>
        </div>
      </section>
    </>
  );
}

function CheckInButton() {
  const [checkedIn, setCheckedIn] = useState(false);

  // V1.1: POST /api/account/appointments/:id/check-in (T1 ask documented
  // in handoff log alongside video endpoints). MVP visual confirmation
  // matches legacy TODO.
  const handleCheckIn = () => {
    if (checkedIn) return;
    setCheckedIn(true);
  };

  return (
    <AnimatePresence mode="wait">
      {checkedIn ? (
        <m.div
          key="success"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className={cn(
            "bg-primary/10 border-primary/20 flex min-h-[56px] items-center justify-center gap-2 rounded-2xl border px-6 py-3",
          )}
        >
          <Check className="text-primary size-5 shrink-0" aria-hidden />
          <span className="text-primary text-sm font-semibold">
            <Trans>Checked in — provider notified</Trans>
          </span>
        </m.div>
      ) : (
        <m.div
          key="button"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          whileTap={{ scale: 0.97 }}
        >
          <Button
            size="lg"
            className="min-h-[56px] w-full rounded-2xl text-base font-semibold"
            onClick={handleCheckIn}
          >
            <Trans>Check in</Trans>
          </Button>
        </m.div>
      )}
    </AnimatePresence>
  );
}

function pickLocalized(
  value: Record<string, string> | string | null | undefined,
  locale: string,
): string | null {
  if (!value) return null;
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value) as Record<string, string>;
      return parsed[locale] ?? parsed.ro ?? parsed.en ?? null;
    } catch {
      return value;
    }
  }
  return value[locale] ?? value.ro ?? value.en ?? null;
}
